import { useState } from 'react'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { H2, H3, SmallParagraph, SmallText } from '../../components/typography/Typography'
import { Button } from '../../components/ui-blocks/Button'
import { EnquiryModal } from '../../components/ContactForm/EnquiryModal'
import { ANALYTICS_EVENTS, track } from '../../lib/analytics'
import vinylFoldableSeating from '../../assets/stadiumImages/vinyl-foldable-seating.jpg'
import hollowOutVipStadiumSeating from '../../assets/stadiumImages/hollow-out-vip-stadium-seating.jpg'
import woodenFoldableTelescopicSeating from '../../assets/stadiumImages/wooden-foldable-telescopic-seating.jpg'
import singleTelescopicSeat from '../../assets/stadiumImages/single-telescopic-seat.jpg'
import retractableTheatreBleacherSeating from '../../assets/stadiumImages/retractable-theatre-bleacher-seating.jpg'
import fabricFoldableSeatingWithArmrests from "../../assets/stadiumImages/fabric-foldable-seating-with-armrests.jpg";
import solidWoodVeneeredFoldableChairs from '../../assets/stadiumImages/solid-wood-veneered-foldable-chairs.jpg'
import bleacherSeatingWithoutBackrest from '../../assets/stadiumImages/bleacher-seating-without-backrest.jpg'
import sportsAudienceSeating from '../../assets/stadiumImages/sports-audience-seating.jpg'

type Product = {
  // Catalog key — doubles as the product page route param and the enquiry's product_id.
  slug: string
  title: string
  subtitle: string
  image: string
}

type Range = {
  label: string
  // Short form used on the tab pill, where horizontal room is tight.
  tabLabel: string
  // Where the full range lives, and the prefix its product pages sit under.
  listingPath: string
  detailPrefix: string
  viewAllLabel: string
  products: Product[]
}

// Curated by the sales team as the best sellers. Every product here is also in
// the catalog under the same slug, so the cards, the product pages and
// contact_submissions.product_id all agree.
const RANGES: Range[] = [
  {
    label: 'Retractable Seating',
    tabLabel: 'Retractable',
    listingPath: '/retractable-seating',
    detailPrefix: '/retractable-seating',
    viewAllLabel: 'View all retractable seating',
    products: [
      {
        slug: 'vinyl-foldable-seating',
        title: 'Vanta',
        subtitle: 'Vinyl Foldable Seating',
        image: vinylFoldableSeating,
      },
      {
        slug: 'fabric-foldable-seating-with-armrests',
        title: 'Lyric',
        subtitle: 'Fabric Foldable Seating with Armrests',
        image: fabricFoldableSeatingWithArmrests,
      },
      {
        slug: 'solid-wood-veneered-foldable-chairs',
        title: 'Valencia',
        subtitle: 'Solid Wood Veneered Foldable Chairs',
        image: solidWoodVeneeredFoldableChairs,
      },
    ],
  },
  {
    label: 'Stadium Seating',
    tabLabel: 'Stadium seating',
    listingPath: '/stadium-seating',
    detailPrefix: '/stadium-seating',
    viewAllLabel: 'View all stadium seating',
    products: [
      {
        slug: 'hollow-out-vip-stadium-seating',
        title: 'Zephyr',
        subtitle: 'Hollow-Out VIP Stadium Seating',
        image: hollowOutVipStadiumSeating,
      },
      {
        slug: 'sports-audience-seating',
        title: 'Rally',
        subtitle: 'Sports Audience Seating',
        image: sportsAudienceSeating,
      },
      {
        slug: 'wooden-foldable-telescopic-seating',
        title: 'Verona',
        subtitle: 'Wooden Foldable Telescopic Seating',
        image: woodenFoldableTelescopicSeating,
      },
    ],
  },
  {
    label: 'Bleachers',
    tabLabel: 'Bleachers',
    listingPath: '/bleachers',
    detailPrefix: '/bleacher-seating',
    viewAllLabel: 'View all bleachers',
    products: [
      {
        slug: 'single-telescopic-seat',
        title: 'Orion',
        subtitle: 'Single Telescopic Seat',
        image: singleTelescopicSeat,
      },
      {
        slug: 'retractable-theatre-bleacher-seating',
        title: 'Maestro',
        subtitle: 'Retractable Theatre Bleacher Seating',
        image: retractableTheatreBleacherSeating,
      },
      {
        slug: 'bleacher-seating-without-backrest',
        title: 'Arc',
        subtitle: 'Bleacher Seating without Backrest',
        image: bleacherSeatingWithoutBackrest,
      },
    ],
  },
]

// Tailwind needs the full class name at build time, so the column count is a map
// rather than an interpolated string.
const LG_COLUMNS: Record<number, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
}

const ProductCard = ({
  product,
  detailPrefix,
  range,
  onEnquire,
}: {
  product: Product
  detailPrefix: string
  range: string
  onEnquire: () => void
}) => (
  <div className='flex w-[72%] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white sm:w-auto sm:shrink'>
    <Link
      to={`${detailPrefix}/${product.slug}`}
      onClick={() =>
        track(ANALYTICS_EVENTS.HOME_PRODUCT_CARD_CLICKED, {
          product: product.title,
          product_id: product.slug,
          range,
          destination: `${detailPrefix}/${product.slug}`,
        })
      }
      className='group flex flex-1 flex-col'
    >
      {/* The catalog photos are cut out on white, so the panel stays white too —
          a tinted panel would frame each photo in a visible white rectangle. */}
      <div className='flex aspect-[3/2] items-center justify-center overflow-hidden p-3 sm:aspect-[4/3] sm:p-4'>
        <img
          src={product.image}
          alt={product.subtitle}
          loading='lazy'
          decoding='async'
          className='h-full w-full object-contain transition-transform duration-500 group-hover:scale-105'
        />
      </div>
      <div className='flex flex-col gap-1 p-4 sm:gap-2 sm:p-5'>
        <H3 className='font-medium'>{product.title}</H3>
        <SmallText className='tracking-[0.15em] text-gray-500'>{product.subtitle}</SmallText>
      </div>
    </Link>
    <div className='px-4 pb-4 sm:px-5 sm:pb-5'>
      <Button variant='primary' className='py-2.5 md:py-3 font-medium' onClick={onEnquire}>
        Get Pricing
      </Button>
    </div>
  </div>
)

export const ProductRange = () => {
  const [activeRangeLabel, setActiveRangeLabel] = useState(RANGES[0].label)
  // The product outlives `isEnquiryOpen` on purpose: clearing it on close would
  // swap the heading to the generic one mid-way through the closing animation.
  const [enquiry, setEnquiry] = useState<Product | null>(null)
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)

  const activeRange = RANGES.find(range => range.label === activeRangeLabel) ?? RANGES[0]

  // Rendered twice — beside the heading on desktop, under the cards on mobile —
  // because the two positions live in different rows of the layout.
  const renderTabs = (className: string) => (
    <div
      role='tablist'
      aria-label='Product ranges'
      className={twMerge(
        'gap-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className
      )}
    >
      {RANGES.map(range => {
        const isActive = range.label === activeRangeLabel

        return (
          <button
            key={range.label}
            type='button'
            role='tab'
            aria-selected={isActive}
            onClick={() => setActiveRangeLabel(range.label)}
            className={twMerge(
              'shrink-0 rounded-full border px-5 py-2.5 font-inter transition-colors md:px-6 md:py-3',
              isActive
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-300 text-gray-600 hover:border-gray-900 hover:text-gray-900'
            )}
          >
            {range.tabLabel}
          </button>
        )
      })}
    </div>
  )

  return (
    <div className='flex flex-col gap-2 bg-white pb-10 md:gap-8 md:pb-16'>
      <div className='flex flex-col gap-4 px-4 md:flex-row md:items-end md:justify-between md:gap-8 md:px-24'>
        <div className='flex flex-col gap-4'>
          <SmallText className='tracking-[0.25em] text-gray-500'>Division 01 — Seating</SmallText>
          <H2 className='max-w-2xl'>Built for the venue, not the catalogue</H2>
        </div>
        {/* From `md` up the tabs ride alongside the heading; below it they sit
            under the cards instead (the copy below). Only one is ever
            displayed, so the hidden one stays out of the a11y tree too. */}
        {renderTabs('hidden shrink-0 md:flex')}
      </div>

      {/* Cards swipe sideways on mobile and sit side by side from `sm` up. Column
          count follows the tile count — products plus the "view all" tile — so a
          short range never leaves an empty column. */}
      <div
        className={twMerge(
          'ml-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible md:px-24 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          LG_COLUMNS[Math.min(activeRange.products.length + 1, 4)]
        )}
      >
        {activeRange.products.map(product => (
          <ProductCard
            key={product.slug}
            product={product}
            detailPrefix={activeRange.detailPrefix}
            range={activeRange.label}
            onEnquire={() => {
              track(ANALYTICS_EVENTS.HOME_PRODUCT_ENQUIRE_CLICKED, {
                product: product.title,
                product_id: product.slug,
                range: activeRange.label,
              })
              setEnquiry(product)
              setIsEnquiryOpen(true)
            }}
          />
        ))}

        <Link
          to={activeRange.listingPath}
          onClick={() =>
            track(ANALYTICS_EVENTS.NAV_LINK_CLICKED, {
              label: activeRange.viewAllLabel,
              href: activeRange.listingPath,
              surface: 'home_product_range',
            })
          }
          className='group flex w-[78%] shrink-0 snap-start flex-col items-start justify-center gap-3 rounded-2xl border border-dashed border-gray-300 p-5 transition-colors hover:border-gray-900 sm:w-auto sm:shrink'
        >
          <span className='flex size-12 items-center justify-center rounded-full border border-gray-300 text-gray-900 transition-colors group-hover:border-gray-900 group-hover:bg-gray-900 group-hover:text-white'>
            <ArrowRightIcon className='size-5' />
          </span>
          <H3 className='font-medium'>{activeRange.viewAllLabel}</H3>
          <SmallParagraph className='text-gray-600'>
            Browse every model in the {activeRange.label.toLowerCase()} range.
          </SmallParagraph>
        </Link>
      </div>

      {renderTabs('flex px-4 pb-1 md:hidden')}

      <EnquiryModal
        open={isEnquiryOpen}
        onClose={() => setIsEnquiryOpen(false)}
        productId={enquiry?.slug}
        productTitle={enquiry?.title}
        productImage={enquiry?.image}
        surface='home_product_range'
      />
    </div>
  )
}
